/** Summarize app-selected objects for the chat composer without exposing IDs. */

export type AppContextChip = {
  key: string
  label: string
  title: string
}

const MAX_CHIPS = 8

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function selectionKind(key: string): string | null {
  if (key === 'previewElements') return 'Page element'
  if (key === 'contextElements') return 'Element'
  if (key === 'selection' || key === 'selected') return 'Item'
  if (!/^selected[A-Z]/.test(key)) return null
  const plural = key
    .slice('selected'.length)
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .toLowerCase()
  const singular = plural.endsWith('ies')
    ? `${plural.slice(0, -3)}y`
    : plural.endsWith('s')
      ? plural.slice(0, -1)
      : plural
  return singular.charAt(0).toUpperCase() + singular.slice(1)
}

function selectedName(value: unknown): string | null {
  const item = record(value)
  if (!item) return null
  for (const key of ['label', 'title', 'name', 'text']) {
    const raw = item[key]
    if (typeof raw !== 'string') continue
    const name = raw.replace(/\s+/g, ' ').trim()
    if (!name) continue
    if (/^(?:[a-z]+:\/\/|[\\/]|~[\\/]|[a-z]:[\\/])/i.test(name)) continue
    if (/[/\\](?:Users|home|workspace)[/\\]/i.test(name)) continue
    if (/^(?:sha256:)?[a-f\d]{32,}$/i.test(name)) continue
    if (/[\\/]\S+\.(?:html?|css|js|json|md|pdf)$/i.test(name)) continue
    return name.length > 72 ? `${name.slice(0, 71)}…` : name
  }
  return null
}

/** Read selected arrays from app model context, including nested document state. */
export function appContextChips(context: unknown): {
  chips: AppContextChip[]
  hiddenCount: number
} {
  const chips: AppContextChip[] = []
  let hiddenCount = 0
  const visit = (value: unknown, path: string, depth: number) => {
    const source = record(value)
    if (!source || depth > 3) return
    for (const [key, entry] of Object.entries(source)) {
      if (Array.isArray(entry)) {
        const kind = selectionKind(key)
        if (!kind) continue
        entry.forEach((item, index) => {
          if (chips.length >= MAX_CHIPS) {
            hiddenCount += 1
            return
          }
          const name = selectedName(item)
          const label = name ? `${kind} · ${name}` : `${kind} ${index + 1}`
          chips.push({ key: `${path}.${key}.${index}`, label, title: label })
        })
      } else if (depth < 3) visit(entry, `${path}.${key}`, depth + 1)
    }
  }
  visit(context, 'context', 0)
  return { chips, hiddenCount }
}
